/**
 * TSS routes that change the account and that a dApp token can no longer call (tss-api #6, D4).
 * With a dApp session the parent sends them through the iframe (ACCOUNT_REQUEST), which uses its own session;
 * the iframe accepts only these. Shared by the parent and the iframe bundle: keep it free of dependencies.
 */

export type AccountRequestMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface AccountRoute {
  method: AccountRequestMethod
  path: RegExp
  /** query parameters the route takes; any other is refused */
  query?: readonly string[]
}

const UNLINKABLE = 'passkey|email|telegram|google|apple|discord|x|wallet'

const ACCOUNT_ROUTES: ReadonlyArray<AccountRoute> = [
  { method: 'PATCH', path: /^\/api\/auth\/profile$/ },
  { method: 'PUT', path: /^\/api\/auth\/nickname$/ },
  { method: 'POST', path: /^\/api\/auth\/devices\/link$/ },
  { method: 'DELETE', path: /^\/api\/auth\/devices\/[A-Za-z0-9_-]{1,128}$/ },
  { method: 'POST', path: /^\/api\/tss\/wallet\/smart-account\/chain$/ },
  // linking and unlinking providers (brief 4)
  { method: 'POST', path: /^\/api\/auth\/link\/wallet$/ },
  { method: 'POST', path: /^\/api\/auth\/link\/telegram$/ },
  { method: 'POST', path: /^\/api\/auth\/link\/email\/(send-code|verify-code)$/ },
  { method: 'POST', path: /^\/api\/auth\/link\/passkey\/(begin|complete)$/ },
  { method: 'POST', path: /^\/api\/auth\/link\/(x|discord)\/start$/ },
  { method: 'DELETE', path: /^\/api\/auth\/unlink\/wallet$/, query: ['address'] },
  { method: 'DELETE', path: new RegExp(`^/api/auth/unlink/(${UNLINKABLE})$`), query: ['externalId'] }
]

/** Path and query of an account request (`/api/auth/unlink/x?externalId=…`); null when it is not one */
export function parseAccountRoute(method: string, pathWithQuery: string): { path: string; query: URLSearchParams } | null {
  if (typeof pathWithQuery !== 'string' || !pathWithQuery.startsWith('/')) return null
  const [path, search = ''] = pathWithQuery.split('?', 2) as [string, string?]
  const query = new URLSearchParams(search)
  const route = ACCOUNT_ROUTES.find((r) => r.method === method && r.path.test(path))
  if (!route) return null
  for (const key of query.keys()) if (!route.query?.includes(key)) return null
  return { path, query }
}

export function isAccountRoute(method: string, path: string): method is AccountRequestMethod {
  return parseAccountRoute(method, path) !== null
}

/** Account routes whose TSS answer carries a new token pair (linking): the iframe never forwards it */
export function isLinkRoute(path: string): boolean {
  return path.startsWith('/api/auth/link/')
}
